import type { Jargon } from '../../types/vertical';

/**
 * Help articles are written vertical-agnostically: they say {{client_label}}
 * rather than "Customer", so the same copy reads correctly for a garage, a
 * salon or a gym. This substitutes the active vertical's jargon before render.
 *
 * Supported forms — modifiers chain with either `|` or `,`, because both read
 * naturally and picking one silently broke every article that guessed the
 * other:
 *   {{client_label}}                → Customer
 *   {{sidebar.expenses}}            → Expenses  (dotted paths reach nested keys)
 *   {{client_label|lower}}          → customer
 *   {{client_label|plural}}         → Customers
 *   {{client_label|plural|lower}}   → customers
 *   {{client_label|lower,plural}}   → customers
 *
 * Unknown keys are left untouched rather than blanked, so a typo shows up as
 * visible text instead of silently deleting a word mid-sentence.
 */

type Modifier = 'lower' | 'upper' | 'plural';

function pluralise(word: string): string {
    if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
    if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
    return `${word}s`;
}

function applyModifier(value: string, modifier: Modifier): string {
    switch (modifier) {
        case 'lower': return value.toLowerCase();
        case 'upper': return value.toUpperCase();
        case 'plural': return pluralise(value);
        default: return value;
    }
}

export function interpolateJargon(markdown: string, jargon: Jargon): string {
    return markdown.replace(/\{\{\s*([a-z_.]+)\s*((?:[|,]\s*[a-z]+\s*)*)\}\}/gi, (whole, key: string, mods?: string) => {
        // Dotted paths reach nested jargon (sidebar.expenses); a plain key is
        // just the one-segment case of the same walk.
        const raw = key.split('.').reduce<unknown>(
            (node, segment) =>
                node && typeof node === 'object'
                    ? (node as Record<string, unknown>)[segment]
                    : undefined,
            jargon as unknown,
        );
        if (typeof raw !== 'string' || !raw) return whole;

        if (!mods) return raw;
        return mods
            .split(/[|,]/)
            .filter(Boolean)
            .map((m) => m.trim() as Modifier)
            // Pluralise before casing so "Vehicle|lower,plural" and
            // "Vehicle|plural,lower" both give "vehicles".
            .sort((a, b) => (a === 'plural' ? -1 : b === 'plural' ? 1 : 0))
            .reduce((acc, m) => applyModifier(acc, m), raw);
    });
}
